import { useEffect, useState, type ReactNode } from "react";
import { anoDaTemporada } from "@legacy-manager/engine";

import type {
  CandidatoATecnico,
  ClubView,
  PromocaoView,
  PropostaPeloGaroto,
  YouthRow,
} from "../game/types";
import { Crest, Money, MoraleFace, Panel, PositionChip, Stars, StrengthBadge } from "../ui/atoms";
import {
  CabecalhoDaTabela,
  CelulasDaLinha,
  ControlesDaTabela,
  LinhaVazia,
  useTabela,
  type Coluna,
} from "../ui/tabela";
import { useDialogoDeAtalhos } from "../ui/useAtalhos";
import { StandingsTable } from "./panels";
import { dinheiro, feitoDoMotor, rotuloDeCaracteristica, rotuloDePosicao, useT, type Chave } from "../i18n";
import "./time-b.css";

/**
 * O Time B.
 *
 * É a base dos clubes humanos: os garotos vêm pela peneira, o técnico dela os
 * forma sozinho, e o treinador principal decide quem sobe, quem é vendido e
 * quem é dispensado. Ninguém é contratado para o Time B. Nada aqui entra na fila de decisões; tudo vale na hora,
 * como a compra imediata, porque é escritório do clube e não jogo da rodada.
 *
 * O cartão do garoto mostra o que ele fez, e não os atributos: os feitos são a
 * pista para o sorteio do Jogador Estrela, e a pista é tudo que o treinador
 * tem. A qualidade escondida que decide o sorteio nunca sai do motor.
 */

/** As ações do Time B, que o hub liga ao worker. */
export interface AcoesDoTimeB {
  /** Sobe o garoto e devolve a carta, ou nulo quando não subiu. */
  promover: (prospectId: string) => Promise<PromocaoView | null>;
  propostas: (prospectId: string) => Promise<PropostaPeloGaroto[]>;
  vender: (prospectId: string, buyerId: string) => void;
  dispensar: (prospectId: string) => void;
  trocarTecnico: (candidateId: string) => void;
}

type Aba = "elenco" | "partida" | "classificacao" | "historico";

/** Rótulo da reputação de técnico, na mesma régua do treinador principal. */
export function rotuloDaReputacaoDoTecnico(reputacao: number): Chave {
  if (reputacao >= 85) return "hub.reputacaoTecnico.elite";
  if (reputacao >= 70) return "hub.reputacaoTecnico.alta";
  if (reputacao >= 50) return "hub.reputacaoTecnico.boa";
  if (reputacao >= 30) return "hub.reputacaoTecnico.regular";
  return "hub.reputacaoTecnico.baixa";
}

/** A chance de 0 a 1 como a tela escreve: inteiro, com o "cerca de" na frente. */
function chanceEscrita(chance: number): string {
  return `~${Math.round(chance * 100)}%`;
}

export function TimeBPanel({
  view,
  busy,
  acoes,
  onIrAoElenco,
}: {
  view: ClubView;
  busy: boolean;
  acoes: AcoesDoTimeB;
  onIrAoElenco: () => void;
}): ReactNode {
  const t = useT();
  const timeB = view.timeB;
  const [aba, setAba] = useState<Aba>("elenco");
  const [marcado, setMarcado] = useState<string | null>(null);
  const [trocaAberta, setTrocaAberta] = useState(false);
  const [sorteio, setSorteio] = useState<YouthRow | null>(null);

  const porForca = [...view.youth].sort((a, b) => b.strength - a.strength);
  const garoto = view.youth.find((entrada) => entrada.id === marcado) ?? porForca[0] ?? null;
  const tecnico = timeB.coach;

  const abas: Array<[Aba, Chave]> = [
    ["elenco", "timeB.abas.elenco"],
    ["partida", "timeB.abas.partida"],
    ["classificacao", "timeB.abas.classificacao"],
    ["historico", "timeB.abas.historico"],
  ];

  return (
    <div className="grid" style={{ gap: 14 }}>
      <section className="card timeb-tecnico" aria-label={t("timeB.tecnico")}>
        <div className="timeb-fatos">
          <Fato rotulo={t("timeB.tecnico")} valor={tecnico?.name ?? t("comum.vazio")} />
          {tecnico && (
            <Fato
              rotulo={t("hub.fatos.reputacaoTecnico")}
              valor={
                <span className="row" style={{ gap: 6 }}>
                  <Stars level={(tecnico.reputation / 100) * 6} size={12} />
                  {t(rotuloDaReputacaoDoTecnico(tecnico.reputation))}
                </span>
              }
            />
          )}
          {tecnico && (
            <Fato
              rotulo={t("timeB.moral")}
              valor={
                <span className="row" style={{ gap: 5 }}>
                  <MoraleFace value={tecnico.morale} size={14} />
                  <span className="num">{tecnico.morale}</span>
                </span>
              }
            />
          )}
          <Fato rotulo={t("timeB.peneira")} valor={t("timeB.peneiraValor", { nivel: timeB.intakeLevel })} />
          {tecnico && (
            <Fato
              rotulo={t("timeB.chanceDeEstrela")}
              valor={<span className="num timeb-ouro">{chanceEscrita(tecnico.starChance)}</span>}
            />
          )}
          <Fato
            rotulo={t("timeB.aspirantes")}
            valor={timeB.position ? t("hub.posicaoValor", { posicao: timeB.position }) : t("comum.vazio")}
          />
          <Fato rotulo={t("timeB.elencoB")} valor={<span className="num">{`${timeB.size}/${timeB.capacity}`}</span>} />
        </div>
        {tecnico && (
          <div className="row timeb-acoes-do-tecnico">
            <button className="ghost" disabled={busy} onClick={() => setTrocaAberta(true)}>
              {t("timeB.trocarTecnico")}
            </button>
          </div>
        )}
      </section>

      <div className="timeb">
        <Panel
          title={t("timeB.titulo")}
          padded={false}
          action={
            <div className="row" role="tablist" style={{ gap: 4, flexWrap: "wrap" }}>
              {abas.map(([chave, rotulo]) => (
                <button
                  key={chave}
                  role="tab"
                  aria-selected={aba === chave}
                  className="ghost timeb-aba"
                  onClick={() => setAba(chave)}
                >
                  {t(rotulo)}
                  {chave === "elenco" && <span className="num timeb-conta">{view.youth.length}</span>}
                </button>
              ))}
            </div>
          }
        >
          {aba === "elenco" && <ElencoB view={view} marcado={garoto?.id ?? null} onMarcar={setMarcado} />}
          {aba === "partida" && <UltimaPartida view={view} />}
          {aba === "classificacao" &&
            (timeB.league ? (
              <div style={{ padding: 12 }}>
                <StandingsTable
                  title={t("timeB.ligaDe", { liga: timeB.league.competitionName })}
                  rows={timeB.league.rows}
                  maxHeight="calc(100vh - 360px)"
                />
              </div>
            ) : (
              <p className="muted" style={{ margin: 0, padding: 14 }}>
                {t("comum.vazio")}
              </p>
            ))}
          {aba === "historico" && <HistoricoCompleto view={view} />}
        </Panel>

        <div className="grid" style={{ gap: 14, alignContent: "start" }}>
          {garoto ? (
            <CartaoDoGaroto
              key={garoto.id}
              garoto={garoto}
              view={view}
              busy={busy}
              acoes={acoes}
              onPromover={() => setSorteio(garoto)}
            />
          ) : (
            <Panel title={t("timeB.garoto")}>
              <p className="muted" style={{ margin: 0 }}>
                {t("comum.vazio")}
              </p>
            </Panel>
          )}
          <ResumoDoHistorico view={view} />
        </div>
      </div>

      {trocaAberta && tecnico && (
        <TrocaDeTecnico
          view={view}
          busy={busy}
          aoContratar={(id) => {
            acoes.trocarTecnico(id);
            setTrocaAberta(false);
          }}
          aoFechar={() => setTrocaAberta(false)}
        />
      )}
      {sorteio && (
        <SorteioDaEstrela
          garoto={sorteio}
          view={view}
          promover={acoes.promover}
          aoFechar={() => setSorteio(null)}
          aoIrAoElenco={() => {
            setSorteio(null);
            onIrAoElenco();
          }}
        />
      )}
    </div>
  );
}

function Fato({ rotulo, valor }: { rotulo: string; valor: ReactNode }): ReactNode {
  return (
    <div className="timeb-fato">
      <span className="label">{rotulo}</span>
      <span className="timeb-fato-valor">{valor}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Elenco B
// ---------------------------------------------------------------------------

function ElencoB({
  view,
  marcado,
  onMarcar,
}: {
  view: ClubView;
  marcado: string | null;
  onMarcar: (id: string) => void;
}): ReactNode {
  const tabela = useTabela<YouthRow>({
    colunas: useColunasDoTimeB(),
    procurar: (garoto) => garoto.name,
    destaqueInicial: "forca",
    ordemInicial: { chave: "forca", direcao: "desc" },
  });
  const garotos = tabela.preparar(view.youth);

  return (
    <div className="scroll" style={{ maxHeight: "calc(100vh - 330px)" }}>
      <ControlesDaTabela tabela={tabela} />
      <table className="fixa">
        <CabecalhoDaTabela tabela={tabela} />
        <tbody>
          {garotos.map((garoto) => (
            <tr
              className="player"
              key={garoto.id}
              aria-selected={garoto.id === marcado}
              tabIndex={0}
              onClick={() => onMarcar(garoto.id)}
              onKeyDown={(evento) => {
                if (evento.key === "Enter" || evento.key === " ") {
                  evento.preventDefault();
                  onMarcar(garoto.id);
                }
              }}
              style={{ cursor: "pointer" }}
            >
              <CelulasDaLinha tabela={tabela} linha={garoto} />
            </tr>
          ))}
          {garotos.length === 0 && <LinhaVazia tabela={tabela} />}
        </tbody>
      </table>
    </div>
  );
}

function useColunasDoTimeB(): Array<Coluna<YouthRow>> {
  const t = useT();
  return [
    {
      chave: "pos",
      rotulo: t("base.colunas.pos"),
      presenca: "sempre",
      largura: 56,
      celula: (garoto) => <PositionChip pos={garoto.pos} />,
    },
    {
      chave: "garoto",
      rotulo: t("base.colunas.garoto"),
      presenca: "sempre",
      ordenar: (garoto) => garoto.name,
      direcaoInicial: "asc",
      celula: (garoto) => (
        <div style={{ minWidth: 0 }}>
          <div className="corta" style={{ fontWeight: 600 }}>
            {garoto.name}
          </div>
          <div className="muted corta" style={{ fontSize: 11 }}>
            {garoto.highlights.map((chave) => rotuloDeCaracteristica(chave)).join(", ")}
          </div>
        </div>
      ),
    },
    {
      chave: "idade",
      rotulo: t("base.colunas.idade"),
      presenca: "destaque",
      largura: 52,
      direita: true,
      ordenar: (garoto) => garoto.age,
      direcaoInicial: "asc",
      celula: (garoto) => <span className="num">{garoto.age}</span>,
    },
    {
      chave: "forca",
      rotulo: t("base.colunas.forca"),
      presenca: "destaque",
      largura: 46,
      direita: true,
      ordenar: (garoto) => garoto.strength,
      celula: (garoto) => <StrengthBadge value={garoto.strength} />,
    },
    {
      chave: "evolucao",
      rotulo: t("base.colunas.evolucao"),
      presenca: "destaque",
      largura: 74,
      direita: true,
      ordenar: (garoto) => garoto.growth,
      celula: (garoto) => (
        <span className="num" style={{ color: garoto.growth > 0 ? "var(--success)" : undefined }}>
          {garoto.growth > 0 ? t("base.evolucaoValor", { pontos: garoto.growth }) : t("comum.vazio")}
        </span>
      ),
    },
    {
      chave: "faixa",
      rotulo: t("base.colunas.faixa"),
      presenca: "destaque",
      largura: 92,
      direita: true,
      ordenar: (garoto) => garoto.potentialHigh,
      celula: (garoto) => (
        <span className="num" style={{ whiteSpace: "nowrap" }}>
          {t("base.faixa", { baixo: garoto.potentialLow, alto: garoto.potentialHigh })}
        </span>
      ),
    },
    {
      chave: "jogos",
      rotulo: t("timeB.colunas.jogos"),
      presenca: "destaque",
      largura: 46,
      direita: true,
      ordenar: (garoto) => garoto.bSeason.apps,
      celula: (garoto) => <span className="num">{garoto.bSeason.apps}</span>,
    },
    {
      chave: "gols",
      rotulo: t("timeB.colunas.gols"),
      presenca: "destaque",
      largura: 46,
      direita: true,
      ordenar: (garoto) => garoto.bSeason.goals,
      celula: (garoto) => <span className="num">{garoto.bSeason.goals || ""}</span>,
    },
    {
      chave: "feitos",
      rotulo: t("timeB.colunas.feitos"),
      presenca: "destaque",
      largura: 60,
      direita: true,
      ordenar: (garoto) => garoto.feats.length,
      celula: (garoto) =>
        garoto.feats.length > 0 ? (
          <span className="num timeb-ouro">{`★ ${garoto.feats.length}`}</span>
        ) : (
          <span className="muted">{t("comum.vazio")}</span>
        ),
    },
    {
      chave: "situacao",
      rotulo: t("base.colunas.situacao"),
      presenca: "destaque",
      largura: 110,
      celula: (garoto) => <SituacaoNoTimeB garoto={garoto} />,
    },
  ];
}

function SituacaoNoTimeB({ garoto }: { garoto: YouthRow }): ReactNode {
  const t = useT();
  if (garoto.injuryRounds > 0) {
    return <span className="chip-escalacao lesionado">{t("elenco.escalacao.lesionado", { rodadas: garoto.injuryRounds })}</span>;
  }
  if (garoto.lastYear) return <span className="chip-escalacao suspenso">{t("timeB.ultimoAno")}</span>;
  if (garoto.stagnant) return <span className="chip-escalacao fora">{t("base.parado")}</span>;
  return <span className="chip-escalacao reserva">{t("timeB.emFormacao")}</span>;
}

// ---------------------------------------------------------------------------
// O cartão do garoto
// ---------------------------------------------------------------------------

function CartaoDoGaroto({
  garoto,
  view,
  busy,
  acoes,
  onPromover,
}: {
  garoto: YouthRow;
  view: ClubView;
  busy: boolean;
  acoes: AcoesDoTimeB;
  onPromover: () => void;
}): ReactNode {
  const t = useT();
  const [confirmar, setConfirmar] = useState<"dispensar" | "vender" | null>(null);
  const [propostas, setPropostas] = useState<PropostaPeloGaroto[] | null>(null);
  const elencoCheio = view.squad.length >= 32;

  async function abrirVenda(): Promise<void> {
    setConfirmar("vender");
    setPropostas(null);
    setPropostas(await acoes.propostas(garoto.id));
  }

  const numeros: Array<[Chave, ReactNode]> = [
    ["timeB.numeros.jogos", garoto.bSeason.apps],
    ["timeB.numeros.gols", garoto.bSeason.goals],
    ["timeB.numeros.assistencias", garoto.bSeason.assists],
    ["timeB.numeros.nota", garoto.bSeason.apps > 0 ? garoto.bSeason.rating.toFixed(1) : t("comum.vazio")],
  ];

  return (
    <section className="card timeb-cartao" aria-label={garoto.name}>
      <div className="timeb-cabeca">
        <div className="timeb-ovr">
          <b className="num">{garoto.strength}</b>
          <small>{t("timeB.ovr")}</small>
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="corta" style={{ fontWeight: 800, fontSize: 16 }}>
            {garoto.name}
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            {t("timeB.linhaDoGaroto", { posicao: rotuloDePosicao(garoto.pos), idade: garoto.age })}
          </div>
          <div className="muted num" style={{ fontSize: 12 }}>
            {t("base.faixa", { baixo: garoto.potentialLow, alto: garoto.potentialHigh })}
          </div>
        </div>
      </div>

      <h3 className="label timeb-subtitulo">{t("timeB.feitos")}</h3>
      {garoto.feats.length > 0 ? (
        <ul className="timeb-feitos">
          {garoto.feats.map((feito, indice) => (
            <li key={`${feito.key}-${feito.season}-${indice}`}>{feitoDoMotor(feito.key, feito.params)}</li>
          ))}
        </ul>
      ) : (
        <p className="muted" style={{ margin: "0 0 12px" }}>
          {t("comum.vazio")}
        </p>
      )}

      <div className="adm-pares adm-pares-2" style={{ marginBottom: 12 }}>
        {numeros.map(([rotulo, valor]) => (
          <div key={rotulo}>
            <span className="muted">{t(rotulo)}</span>
            <span className="num">{valor}</span>
          </div>
        ))}
      </div>

      <div className="row timeb-acoes">
        <button className="timeb-botao-ouro" disabled={busy || elencoCheio} onClick={onPromover}>
          {t("timeB.promover")}
        </button>
        <button className="ghost" disabled={busy || !view.marketOpen} onClick={() => void abrirVenda()}>
          {t("timeB.vender")}
        </button>
        <button className="ghost" style={{ color: "var(--danger)" }} disabled={busy} onClick={() => setConfirmar("dispensar")}>
          {t("timeB.dispensar")}
        </button>
      </div>
      {elencoCheio && <p className="label-muted" style={{ margin: "8px 0 0" }}>{t("timeB.elencoCheio")}</p>}
      {!view.marketOpen && <p className="label-muted" style={{ margin: "8px 0 0" }}>{t("timeB.janelaFechada")}</p>}

      {confirmar === "dispensar" && (
        <div className="timeb-confirmar perigo">
          <span>{t("timeB.dispensarConfirma", { jogador: garoto.name })}</span>
          <div className="row" style={{ gap: 8 }}>
            <button
              className="ghost"
              style={{ color: "var(--danger)" }}
              disabled={busy}
              onClick={() => {
                acoes.dispensar(garoto.id);
                setConfirmar(null);
              }}
            >
              {t("timeB.dispensar")}
            </button>
            <button className="ghost" onClick={() => setConfirmar(null)}>
              {t("comum.cancelar")}
            </button>
          </div>
        </div>
      )}

      {confirmar === "vender" && (
        <div className="timeb-confirmar">
          <span className="label">{t("timeB.propostas")}</span>
          {propostas === null ? (
            <span className="muted">{t("online.carregando")}</span>
          ) : propostas.length === 0 ? (
            <span className="muted">{t("timeB.semPropostas")}</span>
          ) : (
            propostas.map((proposta) => (
              <div key={proposta.clubId} className="row" style={{ gap: 8, justifyContent: "space-between" }}>
                <span className="row" style={{ gap: 8, minWidth: 0 }}>
                  <Crest colors={proposta.colors} short={proposta.clubShort} size={22} />
                  <span className="corta">{proposta.clubName}</span>
                </span>
                <span className="row" style={{ gap: 8 }}>
                  <Money value={proposta.amount} />
                  <button
                    className="ghost"
                    disabled={busy}
                    onClick={() => {
                      acoes.vender(garoto.id, proposta.clubId);
                      setConfirmar(null);
                    }}
                  >
                    {t("timeB.aceitar")}
                  </button>
                </span>
              </div>
            ))
          )}
          <div className="row" style={{ gap: 8 }}>
            <button className="ghost" onClick={() => setConfirmar(null)}>
              {t("timeB.recusar")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Última partida
// ---------------------------------------------------------------------------

function UltimaPartida({ view }: { view: ClubView }): ReactNode {
  const t = useT();
  const partida = view.timeB.lastMatch;
  if (!partida) {
    return (
      <p className="muted" style={{ margin: 0, padding: 14 }}>
        {t("comum.vazio")}
      </p>
    );
  }
  const barras: Array<[Chave, [number, number], string]> = [
    ["relatorios.posse", partida.possession, "%"],
    ["timeB.chutes", partida.shots, ""],
    ["timeB.noGol", partida.onTarget, ""],
  ];
  return (
    <div className="relatorios-duas" style={{ padding: 12 }}>
      <div>
        <div className="row" style={{ gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
          <Crest colors={partida.home.colors} short={partida.home.short} size={26} />
          <strong>{t("timeB.nomeDoB", { clube: partida.home.name })}</strong>
          <span className="num" style={{ fontSize: 22, fontWeight: 800 }}>
            {`${partida.homeGoals} x ${partida.awayGoals}`}
          </span>
          <strong>{t("timeB.nomeDoB", { clube: partida.away.name })}</strong>
          <Crest colors={partida.away.colors} short={partida.away.short} size={26} />
        </div>
        <div className="label-muted" style={{ marginBottom: 12 }}>
          {t("timeB.ligaERodada", { liga: partida.competitionName, rodada: partida.round })}
        </div>
        <ul className="timeb-lances">
          {partida.events.map((lance, indice) => (
            <li key={indice} className={lance.mine ? "meu" : undefined}>
              <span className="num muted">{`${lance.minute}'`}</span>
              <span>
                <i className={`timeb-ev ${lance.kind}`} aria-hidden="true" />
                <span className="sr-only">{t(`timeB.lance.${lance.kind}` as Chave)}</span>
                {lance.name ?? t("timeB.nomeDoB", { clube: lance.clubShort })}
                {lance.kind === "gol" && lance.assist && (
                  <span className="muted">{` · ${t("timeB.assistencia", { jogador: lance.assist })}`}</span>
                )}
                {lance.kind === "lesao" && lance.rounds !== null && (
                  <span className="muted">{` · ${t("timeB.rodadasFora", { rodadas: lance.rounds })}`}</span>
                )}
                {lance.name && !lance.mine && <span className="muted">{` · ${lance.clubShort}`}</span>}
              </span>
            </li>
          ))}
          {partida.events.length === 0 && <li className="muted">{t("comum.vazio")}</li>}
        </ul>
      </div>
      <div>
        {barras.map(([rotulo, [casa, fora], unidade]) => (
          <div key={rotulo} style={{ marginBottom: 10 }}>
            <div className="row" style={{ justifyContent: "space-between", fontSize: 12 }}>
              <span className="num">{`${casa}${unidade}`}</span>
              <span className="label-muted">{t(rotulo)}</span>
              <span className="num">{`${fora}${unidade}`}</span>
            </div>
            <div className="relatorios-barra-dupla">
              <i style={{ width: `${(casa / Math.max(1, casa + fora)) * 100}%` }} />
            </div>
          </div>
        ))}
        <h3 className="label timeb-subtitulo" style={{ marginTop: 14 }}>
          {t("timeB.melhores")}
        </h3>
        <div className="adm-pares">
          {partida.best.map((melhor) => (
            <div key={`${melhor.name}-${melhor.clubShort}`}>
              <span className="corta">
                <b style={{ color: melhor.mine ? "var(--text)" : undefined }}>{melhor.name}</b>
                <span className="muted">{` · ${melhor.clubShort}`}</span>
              </span>
              <span className="num">{melhor.rating.toFixed(1)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Histórico
// ---------------------------------------------------------------------------

function posicaoEscrita(t: ReturnType<typeof useT>, posicao: number, campeao: boolean): string {
  return campeao ? t("timeB.campeao") : t("hub.posicaoValor", { posicao });
}

function HistoricoCompleto({ view }: { view: ClubView }): ReactNode {
  const t = useT();
  const { history, current, position } = view.timeB;
  return (
    <div className="scroll" style={{ maxHeight: "calc(100vh - 330px)" }}>
      <table>
        <thead>
          <tr>
            <th>{t("timeB.historico.temporada")}</th>
            <th>{t("timeB.historico.posicao")}</th>
            <th style={{ textAlign: "right" }}>{t("timeB.historico.pontos")}</th>
            <th>{t("timeB.historico.artilheiro")}</th>
            <th>{t("timeB.historico.subiram")}</th>
            <th style={{ textAlign: "right" }}>{t("timeB.historico.peneira")}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="num">{anoDaTemporada(current.season)}</td>
            <td>{position ? t("timeB.emAndamento", { posicao: t("hub.posicaoValor", { posicao: position }) }) : t("comum.vazio")}</td>
            <td className="num" style={{ textAlign: "right" }}>
              {view.timeB.league?.rows.find((linha) => linha.isMine)?.points ?? 0}
            </td>
            <td>{t("comum.vazio")}</td>
            <td>
              <Subiram lista={current.promoted} />
            </td>
            <td className="num" style={{ textAlign: "right" }}>
              {current.intake}
            </td>
          </tr>
          {history.map((temporada) => (
            <tr key={temporada.season}>
              <td className="num">{anoDaTemporada(temporada.season)}</td>
              <td>{posicaoEscrita(t, temporada.position, temporada.champion)}</td>
              <td className="num" style={{ textAlign: "right" }}>
                {temporada.points}
              </td>
              <td>
                {temporada.topScorer
                  ? t("timeB.artilheiroValor", { jogador: temporada.topScorer.name, gols: temporada.topScorer.goals })
                  : t("comum.vazio")}
              </td>
              <td>
                <Subiram lista={temporada.promoted} />
              </td>
              <td className="num" style={{ textAlign: "right" }}>
                {temporada.intake}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Subiram({ lista }: { lista: Array<{ name: string; star: boolean }> }): ReactNode {
  const t = useT();
  if (lista.length === 0) return <span className="muted">{t("comum.vazio")}</span>;
  return (
    <span>
      {lista.map((entrada, indice) => (
        <span key={`${entrada.name}-${indice}`}>
          {indice > 0 && ", "}
          {entrada.name}
          {entrada.star && <span className="timeb-ouro">{" ★"}</span>}
        </span>
      ))}
    </span>
  );
}

function ResumoDoHistorico({ view }: { view: ClubView }): ReactNode {
  const t = useT();
  const { history, current, position, intakeLevel } = view.timeB;
  const subiram = [...current.promoted, ...history.flatMap((temporada) => temporada.promoted)];
  const estrelas = subiram.filter((entrada) => entrada.star).length;
  return (
    <Panel title={t("timeB.historicoDoTimeB")}>
      <div className="adm-pares">
        <div>
          <span className="muted">{t("timeB.anoEmAndamento", { ano: anoDaTemporada(current.season) })}</span>
          <b>{position ? t("hub.posicaoValor", { posicao: position }) : t("comum.vazio")}</b>
        </div>
        {history.slice(0, 3).map((temporada) => (
          <div key={temporada.season}>
            <span className="muted num">{anoDaTemporada(temporada.season)}</span>
            <b>{posicaoEscrita(t, temporada.position, temporada.champion)}</b>
          </div>
        ))}
        <div>
          <span className="muted">{t("timeB.peneiraDoAno", { ano: anoDaTemporada(current.season) })}</span>
          <b>{t("timeB.chegaram", { total: current.intake, nivel: current.intakeLevel || intakeLevel })}</b>
        </div>
        <div>
          <span className="muted">{t("timeB.subiramAoPrincipal")}</span>
          <b className="num">
            {subiram.length}
            {estrelas > 0 && <span className="timeb-ouro">{` · ★ ${estrelas}`}</span>}
          </b>
        </div>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Troca de técnico
// ---------------------------------------------------------------------------

function TrocaDeTecnico({
  view,
  busy,
  aoContratar,
  aoFechar,
}: {
  view: ClubView;
  busy: boolean;
  aoContratar: (candidateId: string) => void;
  aoFechar: () => void;
}): ReactNode {
  const t = useT();
  useDialogoDeAtalhos({ nome: "TrocaDeTecnico", nivel: 25, aoFechar });
  const atual = view.timeB.coach;
  if (!atual) return null;
  const semCaixa = atual.fee > view.club.cash;

  const linha = (
    chave: string,
    tecnico: Pick<CandidatoATecnico, "name" | "reputation" | "salary" | "starChance">,
    candidato: CandidatoATecnico | null,
  ): ReactNode => (
    <tr key={chave} className={candidato ? undefined : "timeb-atual"}>
      <td>
        <b style={{ fontWeight: 600 }}>{tecnico.name}</b>
        {!candidato && <span className="chip-escalacao titular" style={{ marginLeft: 8 }}>{t("timeB.atual")}</span>}
      </td>
      <td>
        <span className="row" style={{ gap: 6 }}>
          <Stars level={(tecnico.reputation / 100) * 6} size={11} />
          <span className="muted">{t(rotuloDaReputacaoDoTecnico(tecnico.reputation))}</span>
        </span>
      </td>
      <td className="num timeb-ouro" style={{ textAlign: "right" }}>
        {chanceEscrita(tecnico.starChance)}
      </td>
      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
        <Money value={tecnico.salary} />
      </td>
      <td style={{ textAlign: "right" }}>
        {candidato && (
          <button className="ghost" disabled={busy || semCaixa} onClick={() => aoContratar(candidato.id)}>
            {t("timeB.contratarTecnico")}
          </button>
        )}
      </td>
    </tr>
  );

  return (
    <div className="timeb-fundo" role="dialog" aria-modal="true" aria-label={t("timeB.tecnico")} onClick={aoFechar}>
      <div className="card timeb-modal" onClick={(evento) => evento.stopPropagation()}>
        <h3 className="label timeb-subtitulo">{t("timeB.tecnico")}</h3>
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>{t("timeB.colunas.tecnico")}</th>
                <th>{t("hub.fatos.reputacaoTecnico")}</th>
                <th style={{ textAlign: "right" }}>{t("timeB.colunas.estrela")}</th>
                <th style={{ textAlign: "right" }}>{t("timeB.colunas.salario")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {linha("atual", atual, null)}
              {view.timeB.candidates.map((candidato) => linha(candidato.id, candidato, candidato))}
            </tbody>
          </table>
        </div>
        <div className="row" style={{ justifyContent: "space-between", marginTop: 12, gap: 12 }}>
          <span className="muted">{t("timeB.multa", { tecnico: atual.name })}</span>
          <b className="num" style={{ color: semCaixa ? "var(--danger)" : undefined }}>
            {dinheiro(atual.fee)}
          </b>
        </div>
        <div className="row" style={{ justifyContent: "flex-end", marginTop: 14 }}>
          <button className="ghost" onClick={aoFechar}>
            {t("comum.fechar")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// O sorteio do Jogador Estrela
// ---------------------------------------------------------------------------

type Fase = "chance" | "girando" | "revelada";

/**
 * A carta que vira.
 *
 * O sorteio acontece no motor, na hora em que o treinador confirma; a carta só
 * mostra o resultado com um giro de um segundo e meio. Com movimento reduzido
 * ela aparece virada direto.
 */
function SorteioDaEstrela({
  garoto,
  view,
  promover,
  aoFechar,
  aoIrAoElenco,
}: {
  garoto: YouthRow;
  view: ClubView;
  promover: (prospectId: string) => Promise<PromocaoView | null>;
  aoFechar: () => void;
  aoIrAoElenco: () => void;
}): ReactNode {
  const t = useT();
  const [fase, setFase] = useState<Fase>("chance");
  const [resultado, setResultado] = useState<PromocaoView | null>(null);
  const tecnico = view.timeB.coach;
  useDialogoDeAtalhos({ nome: "SorteioDaEstrela", nivel: 26, aoFechar: fase === "girando" ? null : fase === "revelada" ? aoIrAoElenco : aoFechar });

  useEffect(() => {
    if (fase !== "girando") return;
    const reduzido = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    const relogio = window.setTimeout(() => setFase("revelada"), reduzido ? 50 : 1550);
    return () => window.clearTimeout(relogio);
  }, [fase]);

  async function sortear(): Promise<void> {
    const carta = await promover(garoto.id);
    if (!carta) {
      aoFechar();
      return;
    }
    setResultado(carta);
    setFase("girando");
  }

  return (
    <div className="timeb-fundo" role="dialog" aria-modal="true" aria-label={t("timeB.promoverTitulo")}>
      <div className="card timeb-modal">
        {fase === "chance" ? (
          <>
            <h3 className="label timeb-subtitulo">{t("timeB.promoverTitulo")}</h3>
            <div className="timeb-cabeca">
              <div className="timeb-ovr">
                <b className="num">{garoto.strength}</b>
                <small>{t("timeB.ovr")}</small>
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 16 }}>{garoto.name}</div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {t("timeB.linhaComFeitos", { posicao: rotuloDePosicao(garoto.pos), idade: garoto.age, feitos: garoto.feats.length })}
                </div>
              </div>
            </div>
            {tecnico && (
              <div className="timeb-chance">
                <span className="timeb-ouro" style={{ fontWeight: 700 }}>
                  {t("timeB.estrela")}
                </span>
                <div className="relatorios-barra">
                  <i style={{ width: `${Math.round(tecnico.starChance * 100)}%`, background: "var(--accent)" }} />
                </div>
                <b className="num">{chanceEscrita(tecnico.starChance)}</b>
              </div>
            )}
            {tecnico && (
              <p className="label-muted" style={{ margin: "0 0 4px" }}>
                {`${tecnico.name} · ${t(rotuloDaReputacaoDoTecnico(tecnico.reputation))}`}
              </p>
            )}
            <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
              <button className="ghost" onClick={aoFechar}>
                {t("comum.cancelar")}
              </button>
              <button className="timeb-botao-ouro" onClick={() => void sortear()}>
                {t("timeB.promover")}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="timeb-palco">
              <div className={`timeb-carta ${fase === "girando" ? "gira" : "revelada"}`}>
                <div className="timeb-face timeb-verso">
                  <b>{t("comum.marcaLinha1")}</b>
                </div>
                {resultado && (
                  <div className={`timeb-face timeb-frente ${resultado.star ? "estrela" : "comum"}`}>
                    <span className="timeb-nivel">{resultado.star ? t("timeB.jogadorEstrela") : t("timeB.promovido")}</span>
                    <span className="num timeb-grande">{resultado.strength}</span>
                    <span className="timeb-quem">{resultado.name}</span>
                    <span className="timeb-pot">
                      {t("timeB.potencial")}{" "}
                      <b className="num">{`${resultado.potentialLow}–${resultado.potentialHigh}`}</b>
                    </span>
                    {resultado.star && (
                      <span className="timeb-pot">
                        {t("timeB.moralDoElenco")} <b className="num">+3</b>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            {fase === "revelada" && (
              <div className="row" style={{ justifyContent: "flex-end", marginTop: 14 }}>
                <button className="primary" onClick={aoIrAoElenco}>
                  {t("timeB.irAoElenco")}
                </button>
              </div>
            )}
            <p className="sr-only" aria-live="polite">
              {fase === "revelada" && resultado
                ? resultado.star
                  ? t("timeB.jogadorEstrela")
                  : t("timeB.promovido")
                : ""}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
